import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarSearch, Plus, Star, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * iter87.491 — Event Bindings manager (Product Ops → Templates), narrowed
 * to Golf only. Golf is the only template shape whose live event is picked
 * from a curated, admin-managed calendar (Slash Golf season schedule) —
 * every other shape binds to its event via a hardcoded config (API-Football
 * league_id/season, API-Sports league 1, Highlightly league id), so there
 * is nothing to curate for them here. See CHANGELOG.md iter87.491.
 */
export default function TemplateBindingsCard() {
    const [bindings, setBindings] = useState([]);
    const [fallback, setFallback] = useState(null);
    const [season, setSeason] = useState(new Date().getFullYear());
    const [schedule, setSchedule] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState("");
    const [loadingSchedule, setLoadingSchedule] = useState(false);

    const load = useCallback(async () => {
        try {
            const d = await api.siteListTemplateBindings();
            setBindings(d.bindings || []);
            setFallback(d.fallback || null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load bindings");
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const patch = async (id, body) => {
        try { await api.sitePatchTemplateBinding(id, body); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    };
    const remove = async (id) => {
        try { await api.siteDeleteTemplateBinding(id); toast.success("Binding removed"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    const golfCurated = bindings.filter(b => b.source === "slash_golf");
    const golfRows = golfCurated.length ? golfCurated : (fallback ? [fallback] : []);
    const loadSchedule = async () => {
        setLoadingSchedule(true);
        try {
            const d = await api.siteGolfSchedule(season);
            setSchedule(d);
            setSelectedEvent("");
            if (!(d.events || []).length) toast.warning("No events returned for that season");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Schedule fetch failed");
        }
        setLoadingSchedule(false);
    };
    const addGolfBinding = async () => {
        const ev = (schedule?.events || []).find(e => e.event_id === selectedEvent);
        if (!ev) return;
        try {
            await api.siteCreateTemplateBinding({
                source: "slash_golf", tour: schedule.tour, season: schedule.season,
                event_id: ev.event_id, label: ev.name,
            });
            toast.success(`Added ${ev.name}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Add failed");
        }
    };

    const Row = ({ b }) => (
        <div className="flex items-center gap-2 border border-[#222] px-3 py-2 text-xs" data-testid={`binding-row-${b.event_id || b.id}`}>
            <button
                data-testid={`binding-default-btn-${b.id}`}
                title={b.is_default ? "Default event" : "Make default"}
                onClick={() => !b.builtin && patch(b.id, { is_default: true })}
                className={b.is_default ? "text-volt" : "text-zinc-600 hover:text-volt"}
            >
                <Star className="w-3.5 h-3.5" fill={b.is_default ? "currentColor" : "none"} />
            </button>
            <span className="font-bold text-zinc-200">{b.label}</span>
            <span className="text-zinc-500 font-mono">{`#${b.event_id} · ${b.season} · ${(b.tour || "pga").toUpperCase()}`}</span>
            {b.builtin && <span className="pill bg-zinc-800 text-zinc-400 text-[9px] px-1.5 py-0">BUILTIN FALLBACK</span>}
            <span className="flex-1" />
            {!b.builtin && (
                <>
                    <button
                        data-testid={`binding-toggle-btn-${b.id}`}
                        onClick={() => patch(b.id, { enabled: !b.enabled })}
                        className={`pill text-[9px] px-2 py-0.5 border ${b.enabled ? "bg-cyber/15 border-cyber text-cyber" : "bg-zinc-900 border-zinc-700 text-zinc-500"}`}
                    >
                        {b.enabled ? "ENABLED" : "DISABLED"}
                    </button>
                    <button data-testid={`binding-delete-btn-${b.id}`} onClick={() => remove(b.id)} className="text-zinc-600 hover:text-heat">
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </>
            )}
        </div>
    );

    return (
        <div className="brutal-card p-4 space-y-4" data-testid="template-bindings-card">
            <div>
                <h3 className="font-display font-extrabold text-sm uppercase">Event Bindings</h3>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                    Which live Golf events the Golf templates may bind to — curated from the Slash Golf
                    schedule. League wizards only offer <b>enabled</b> events; ⭐ marks the default. Every
                    other template shape binds to a hardcoded vendor feed, so there's nothing to curate
                    for it here — see Vendor Data Cascade below to refresh those.
                </p>
            </div>

            <div className="space-y-3" data-testid="bindings-golf-panel">
                <div className="space-y-1.5" data-testid="template-bindings-list">
                    {golfRows.map(b => <Row key={b.id} b={b} />)}
                </div>
                <div className="border-t border-[#222] pt-3 space-y-2">
                    <p className="text-[10px] uppercase font-bold text-zinc-500">Add from season schedule</p>
                    <div className="flex flex-wrap items-center gap-2">
                        <input data-testid="bindings-season-input" type="number" value={season}
                            onChange={(e) => setSeason(parseInt(e.target.value) || season)}
                            className="brutal-input w-24 font-mono text-sm" />
                        <button data-testid="bindings-load-schedule-btn" onClick={loadSchedule} disabled={loadingSchedule}
                                className="brutal-btn-ghost text-xs flex items-center gap-1.5 disabled:opacity-40">
                            <CalendarSearch className="w-3.5 h-3.5" /> {loadingSchedule ? "Loading…" : "Load schedule"}
                        </button>
                        {schedule && (
                            <>
                                <select data-testid="bindings-event-select" value={selectedEvent}
                                    onChange={(e) => setSelectedEvent(e.target.value)}
                                    className="brutal-input text-xs max-w-[280px]">
                                    <option value="">— pick an event ({(schedule.events || []).length}) —</option>
                                    {(schedule.events || []).map(ev => (
                                        <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>
                                    ))}
                                </select>
                                <button data-testid="bindings-add-btn" onClick={addGolfBinding} disabled={!selectedEvent}
                                        className="brutal-btn-primary text-xs flex items-center gap-1 disabled:opacity-40">
                                    <Plus className="w-3.5 h-3.5" /> Add
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
